import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { brand, projectTypes, navLinks } from "../../mock";
import { Logo, useGoToSection } from "./Header";
import { submitContact, errMsg } from "../../lib/api";

export const saveLead = null; // replaced by backend API

const Label = ({ children }) => <label className="block text-white/50 text-[12px] tracking-[0.15em] uppercase mb-2">{children}</label>;
export const inputCls = "w-full bg-transparent border-0 border-b border-white/20 text-white py-3 outline-none focus:border-yellow-400 transition-colors placeholder:text-white/25";

export function Contact() {
  const empty = { firstName: "", lastName: "", email: "", projectType: "", message: "" };
  const [f, setF] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.firstName || !f.email || !f.message) return toast.error("Please fill in your name, email and project details.");
    setSending(true);
    try {
      await submitContact(f);
      setF(empty);
      toast.success("Message sent! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const items = [
    [Phone, "Phone", brand.phone, brand.phoneHref],
    [Mail, "Email", brand.email, `mailto:${brand.email}`],
  ];

  return (
    <section id="contact" className="bg-[#141416] py-28">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%] grid lg:grid-cols-2 gap-16">
        <div className="reveal">
          <h2 className="font-display font-bold text-white text-[44px] sm:text-[58px] leading-[1] tracking-[-0.03em]">
            Let's build something <span className="text-yellow-400">great</span> together
          </h2>
          <div className="mt-12 space-y-7">
            {items.map(([I, l, v, h]) => (
              <a key={l} href={h} className="group flex items-center gap-5">
                <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-yellow-400 group-hover:border-yellow-400 transition-colors">
                  <I className="w-5 h-5 text-yellow-400 group-hover:text-black transition-colors" />
                </span>
                <div>
                  <div className="text-white/45 text-[12px] tracking-[0.15em]">{l.toUpperCase()}</div>
                  <div className="text-white text-lg group-hover:text-yellow-400 transition-colors">{v}</div>
                </div>
              </a>
            ))}
            <div className="flex items-center gap-5">
              <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center"><MapPin className="w-5 h-5 text-yellow-400" /></span>
              <div>
                <div className="text-white/45 text-[12px] tracking-[0.15em]">OFFICE</div>
                <div className="text-white text-lg">{brand.address1}<br />{brand.address2}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="reveal rounded-3xl bg-zinc-900 border border-white/10 p-8 sm:p-10 space-y-7" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-7">
            <div><Label>First Name</Label><input data-testid="contact-first-name" className={inputCls} value={f.firstName} onChange={set("firstName")} /></div>
            <div><Label>Last Name</Label><input data-testid="contact-last-name" className={inputCls} value={f.lastName} onChange={set("lastName")} /></div>
          </div>
          <div><Label>Email Address</Label><input type="email" data-testid="contact-email" className={inputCls} value={f.email} onChange={set("email")} /></div>
          <div>
            <Label>Project Type</Label>
            <Select value={f.projectType} onValueChange={(v) => setF({ ...f, projectType: v })}>
              <SelectTrigger data-testid="contact-project-type" className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-white focus:ring-0 focus:border-yellow-400 h-12">
                <SelectValue placeholder="Select a category..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                {projectTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Tell us about your project</Label><textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} value={f.message} onChange={set("message")} /></div>
          <button disabled={sending} data-testid="contact-submit" className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold tracking-[0.12em] py-4 rounded-2xl hover:bg-yellow-300 disabled:opacity-60 transition-colors">
            {sending ? "SENDING..." : <>SEND MESSAGE <Send className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}

export function Footer() {
  const go = useGoToSection();
  return (
    <footer className="bg-black border-t border-white/10 py-12">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%] flex flex-col md:flex-row gap-8 items-center justify-between">
        <Logo />
        <nav className="flex flex-wrap justify-center gap-6">
          {navLinks.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="text-white/55 text-sm hover:text-yellow-400 transition-colors">{l.label}</button>
          ))}
        </nav>
        <button onClick={() => go("home")} data-testid="back-to-top" className="w-11 h-11 rounded-full border border-white/20 text-white hover:bg-yellow-400 hover:text-black transition-colors flex items-center justify-center"><ArrowUp className="w-4 h-4" /></button>
      </div>
      <div className="max-w-[1500px] mx-auto px-6 lg:px-[13.3%] mt-8 pt-6 border-t border-white/5 text-center text-white/35 text-[13px]">
        &copy; {new Date().getFullYear()} {brand.legal} All rights reserved.
      </div>
    </footer>
  );
}
